import React, { useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Grid, Chip, Paper, Button, CircularProgress, useTheme, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Step, StepLabel, Stepper } from '@mui/material';
import { Icon } from '@iconify/react';
import { Transaction } from './TransactionsTable';
import { useToast } from './ui/ToastProvider';
interface TransactionDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction;
  onAction?: (actionType: string, reason?: string) => Promise<void>;
}
export const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = ({
  transaction,
  open,
  onClose,
  onAction
}) => {
  const [expandedState, setExpandedState] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showToast
  } = useToast();
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  const formatDuration = (ms?: number) => {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };
  const getStatusColor = (status: string) => {
    const colors: Record<string, {
      bg: string;
      color: string;
    }> = {
      IN_PROGRESS: {
        bg: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6'
      },
      SUCCESS: {
        bg: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981'
      },
      FAILED: {
        bg: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444'
      },
      IN_REVIEW: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      },
      PENDING_INQUIRY: {
        bg: 'rgba(139, 92, 246, 0.1)',
        color: '#8B5CF6'
      },
      PASS: {
        bg: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981'
      },
      FAIL: {
        bg: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444'
      },
      PENDING: {
        bg: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6'
      },
      REVIEW: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      }
    };
    return colors[status] || {
      bg: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B'
    };
  };
  const getStateIcon = (stateCode: string) => {
    const icons: Record<string, string> = {
      SCREENING: 'lucide:shield-check',
      TITLE_FETCH: 'lucide:id-card',
      RAAST_TITLE_FETCH: 'lucide:id-card',
      ONELINK_TITLE_FETCH: 'lucide:id-card',
      BALANCE_CHECK: 'lucide:building-2',
      RTGS_BALANCE_CHECK: 'lucide:building-2',
      FT_POST: 'lucide:send',
      RAAST_POST: 'lucide:send',
      ONELINK_POST: 'lucide:send',
      RTGS_POST: 'lucide:send',
      SUCCESS: 'lucide:check-circle',
      FAILED: 'lucide:x-circle'
    };
    return icons[stateCode] || 'lucide:circle';
  };
  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      PASS: 'lucide:check',
      FAIL: 'lucide:x',
      PENDING: 'lucide:clock',
      REVIEW: 'lucide:alert-triangle'
    };
    return icons[status] || 'lucide:help-circle';
  };
  const handleAction = async (actionType: string) => {
    if (!onAction) return;
    setActionLoading(true);
    try {
      await onAction(actionType);
      showToast(`${actionType} completed successfully`, 'success');
    } catch (error) {
      showToast(`${actionType} failed`, 'error');
    } finally {
      setActionLoading(false);
    }
  };
  const renderOverviewAccordion = () => <Accordion defaultExpanded data-id="element-861">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-863" />} data-id="element-862">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-864">
          <Icon icon="lucide:info" width={20} color="#0EA5E9" data-id="element-865" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-866">
            Overview
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-867">
        <Grid container spacing={2} data-id="element-868">
          <Grid item xs={12} sm={6} data-id="element-869">
            <Typography variant="caption" color="text.secondary" data-id="element-870">
              Transaction ID
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-871">
              {transaction.id}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-872">
            <Typography variant="caption" color="text.secondary" data-id="element-873">
              Bank Reference
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-874">
              {transaction.bankRef}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-875">
            <Typography variant="caption" color="text.secondary" data-id="element-876">
              Partner Name
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-877">
              {transaction.mtoName || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-878">
            <Typography variant="caption" color="text.secondary" data-id="element-879">
              Partner Region
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-880">
              {transaction.region}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-881">
            <Typography variant="caption" color="text.secondary" data-id="element-882">
              Created At
            </Typography>
            <Typography variant="body2" data-id="element-883">
              {formatDate(transaction.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-884">
            <Typography variant="caption" color="text.secondary" data-id="element-885">
              Last Updated
            </Typography>
            <Typography variant="body2" data-id="element-886">
              {formatDate(transaction.updatedAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-887">
            <Typography variant="caption" color="text.secondary" data-id="element-888">
              Current State
            </Typography>
            <Chip label={(transaction.currentState || 'UNKNOWN').replace(/_/g, ' ')} size="small" sx={{
            backgroundColor: 'rgba(100, 116, 139, 0.1)',
            color: '#64748B',
            fontWeight: 600,
            fontSize: '0.7rem',
            mt: 0.5
          }} data-id="element-889" />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderPartiesAccordion = () => <Accordion data-id="element-890">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-892" />} data-id="element-891">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-893">
          <Icon icon="lucide:users" width={20} color="#0EA5E9" data-id="element-894" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-895">
            Parties
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-896">
        <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-897">
          Remitter
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        mb: 3,
        borderRadius: 2
      }} data-id="element-898">
          <Typography variant="body1" fontWeight={600} data-id="element-899">
            {transaction.remitterName}
          </Typography>
        </Paper>
        <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-900">
          Beneficiary
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        borderRadius: 2
      }} data-id="element-901">
          <Typography variant="body1" fontWeight={600} data-id="element-902">
            {transaction.beneficiaryName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
          mt: 1
        }} data-id="element-903">
            IBAN: {transaction.beneficiaryIBAN}
          </Typography>
        </Paper>
      </AccordionDetails>
    </Accordion>;
  const renderAccountsAccordion = () => <Accordion data-id="element-904">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-906" />} data-id="element-905">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-907">
          <Icon icon="lucide:building-2" width={20} color="#0052CC" data-id="element-908" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-909">
            Accounts
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-910">
        <Typography variant="subtitle2" gutterBottom data-id="element-911">
          Beneficiary Account
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        borderRadius: 2
      }} data-id="element-912">
          <Grid container spacing={2} data-id="element-913">
            <Grid item xs={12} data-id="element-914">
              <Typography variant="caption" color="text.secondary" data-id="element-915">
                IBAN
              </Typography>
              <Typography variant="body2" fontFamily="monospace" data-id="element-916">
                {transaction.beneficiaryIBAN}
              </Typography>
            </Grid>
            <Grid item xs={12} data-id="element-917">
              <Typography variant="caption" color="text.secondary" data-id="element-918">
                Account Title
              </Typography>
              <Typography variant="body2" data-id="element-919">
                {transaction.beneficiaryName}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>;
  const renderAmountsAccordion = () => <Accordion data-id="element-920">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-922" />} data-id="element-921">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-923">
          <Icon icon="lucide:banknote" width={20} color="#10B981" data-id="element-924" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-925">
            Amounts & FX
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-926">
        <Grid container spacing={2} data-id="element-927">
          <Grid item xs={12} sm={6} data-id="element-928">
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-929">
              <Typography variant="caption" color="text.secondary" data-id="element-930">
                Foreign Currency Amount
              </Typography>
              <Typography variant="h6" color="primary.main" data-id="element-931">
                {transaction.currencyFCY} {transaction.amountFCY}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-932">
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-933">
              <Typography variant="caption" color="text.secondary" data-id="element-934">
                PKR Amount
              </Typography>
              <Typography variant="h6" color="primary.main" data-id="element-935">
                PKR {transaction.amountPKR}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} data-id="element-936">
            <Typography variant="caption" color="text.secondary" data-id="element-937">
              Purpose
            </Typography>
            <Typography variant="body1" data-id="element-938">
              {transaction.purposeText} ({transaction.purposeCode})
            </Typography>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderComplianceAccordion = () => {
    const screeningState = transaction.stateHistory.find(s => s.stateCode === 'SCREENING');
    const amlStatus = screeningState ? screeningState.status === 'PASS' ? 'PASS' : 'REVIEW' : 'PENDING';
    const kycStatus = screeningState ? screeningState.status === 'PASS' ? 'PASS' : 'FAIL' : 'PENDING';
    return <Accordion data-id="element-939">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-941" />} data-id="element-940">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-942">
            <Icon icon="lucide:shield-check" width={20} color="#F59E0B" data-id="element-943" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-944">
              Compliance & Screening
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-945">
          <Grid container spacing={2} data-id="element-946">
            <Grid item xs={12} sm={6} data-id="element-947">
              <Typography variant="subtitle2" gutterBottom data-id="element-948">
                AML Check
              </Typography>
              <Paper variant="outlined" sx={{
              p: 2,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }} data-id="element-949">
                <Chip icon={<Icon icon={getStatusIcon(amlStatus)} width={14} data-id="element-951" />} label={amlStatus} size="small" sx={{
                ...getStatusColor(amlStatus),
                fontWeight: 600
              }} data-id="element-950" />
                <Typography variant="body2" color="text.secondary" data-id="element-952">
                  {amlStatus === 'PASS' ? 'No matches found' : amlStatus === 'REVIEW' ? 'Manual review required' : 'Pending check'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} data-id="element-953">
              <Typography variant="subtitle2" gutterBottom data-id="element-954">
                KYC Verification
              </Typography>
              <Paper variant="outlined" sx={{
              p: 2,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }} data-id="element-955">
                <Chip icon={<Icon icon={getStatusIcon(kycStatus)} width={14} data-id="element-957" />} label={kycStatus} size="small" sx={{
                ...getStatusColor(kycStatus),
                fontWeight: 600
              }} data-id="element-956" />
                <Typography variant="body2" color="text.secondary" data-id="element-958">
                  {kycStatus === 'PASS' ? 'Identity verified' : kycStatus === 'FAIL' ? 'Verification failed' : 'Pending verification'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} data-id="element-959">
              <Typography variant="subtitle2" gutterBottom data-id="element-960">
                Screening Status
              </Typography>
              <Paper variant="outlined" sx={{
              p: 2,
              borderRadius: 2
            }} data-id="element-961">
                {screeningState ? <>
                    <Chip icon={<Icon icon={getStatusIcon(screeningState.status)} width={14} data-id="element-963" />} label={screeningState.status} size="small" sx={{
                  ...getStatusColor(screeningState.status),
                  fontWeight: 600
                }} data-id="element-962" />
                    <Typography variant="body2" color="text.secondary" sx={{
                  mt: 1
                }} data-id="element-964">
                      Completed: {formatDate(screeningState.timestamp)}
                    </Typography>
                  </> : <Typography variant="body2" color="text.secondary" data-id="element-965">
                    Not yet screened
                  </Typography>}
              </Paper>
            </Grid>
            {transaction.ePRCNo && <Grid item xs={12} data-id="element-966">
                <Typography variant="subtitle2" gutterBottom data-id="element-967">
                  e-PRC Details
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              borderRadius: 2
            }} data-id="element-968">
                  <Typography variant="body2" data-id="element-969">
                    <strong data-id="element-970">e-PRC Number:</strong> {transaction.ePRCNo}
                  </Typography>
                  {transaction.ePRCDate && <Typography variant="body2" sx={{
                mt: 1
              }} data-id="element-971">
                      <strong data-id="element-972">Issue Date:</strong>{' '}
                      {formatDate(transaction.ePRCDate)}
                    </Typography>}
                </Paper>
              </Grid>}
          </Grid>
        </AccordionDetails>
      </Accordion>;
  };
  const renderLifecycleAccordion = () => {
    const steps = ['Initiated', 'Partner Validated', 'Compliance Check', 'Processing', 'Settlement', 'Completed'];
    let activeStep = 0;
    if (transaction.stateHistory.length > 0) activeStep = 1;
    if (transaction.stateHistory.some(s => s.stateCode === 'SCREENING')) activeStep = 2;
    if (transaction.stateHistory.some(s => s.stateCode === 'TITLE_FETCH' || s.stateCode === 'RAAST_TITLE_FETCH')) activeStep = 3;
    if (transaction.lifecycleStatus === 'SUCCESS') activeStep = 6;else if (transaction.lifecycleStatus === 'FAILED') activeStep = transaction.stateHistory.length;
    return <Accordion data-id="element-973">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-975" />} data-id="element-974">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-976">
            <Icon icon="lucide:git-commit" width={20} color="#0052CC" data-id="element-977" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-978">
              Lifecycle & Settlement
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-979">
          <Box sx={{
          width: '100%',
          mb: 4,
          mt: 2
        }} data-id="element-980">
            <Stepper activeStep={activeStep} alternativeLabel data-id="element-981">
              {steps.map((label, index) => {
              const isFailed = transaction.lifecycleStatus === 'FAILED' && index === activeStep;
              return <Step key={label} data-id="element-982">
                    <StepLabel error={isFailed} data-id="element-983">{label}</StepLabel>
                  </Step>;
            })}
            </Stepper>
          </Box>
        </AccordionDetails>
      </Accordion>;
  };
  const renderSettlementAccordion = () => <Accordion data-id="element-984">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-986" />} data-id="element-985">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-987">
          <Icon icon="lucide:landmark" width={20} color="#0052CC" data-id="element-988" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-989">
            Settlement Details
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-990">
        <Grid container spacing={2} data-id="element-991">
          <Grid item xs={12} sm={6} data-id="element-992">
            <Typography variant="caption" color="text.secondary" data-id="element-993">
              Settlement Status
            </Typography>
            <Box sx={{
            mt: 0.5
          }} data-id="element-994">
              <Chip label={transaction.lifecycleStatus === 'SUCCESS' ? 'SETTLED' : 'PENDING'} size="small" sx={{
              backgroundColor: transaction.lifecycleStatus === 'SUCCESS' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
              color: transaction.lifecycleStatus === 'SUCCESS' ? '#10B981' : '#F59E0B',
              fontWeight: 600
            }} data-id="element-995" />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-996">
            <Typography variant="caption" color="text.secondary" data-id="element-997">
              Settlement Date
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-998">
              {transaction.lifecycleStatus === 'SUCCESS' ? formatDate(transaction.updatedAt) : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-999">
            <Typography variant="caption" color="text.secondary" data-id="element-1000">
              Settlement Reference
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-1001">
              {transaction.lifecycleStatus === 'SUCCESS' ? `SET-${transaction.id.split('-')[1]}` : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-1002">
            <Typography variant="caption" color="text.secondary" data-id="element-1003">
              Bank Confirmation
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-1004">
              {transaction.lifecycleStatus === 'SUCCESS' ? 'Confirmed' : '—'}
            </Typography>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderTimelineAccordion = () => {
    if (transaction.stateHistory.length === 0) {
      return <Accordion data-id="element-1005">
          <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-1007" />} data-id="element-1006">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1008">
              <Icon icon="lucide:clock" width={20} color="#0052CC" data-id="element-1009" />
              <Typography variant="subtitle2" fontWeight={600} data-id="element-1010">
                Timeline
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-id="element-1011">
            <Box sx={{
            py: 4,
            textAlign: 'center'
          }} data-id="element-1012">
              <Icon icon="lucide:clock" width={48} height={48} color="#94A3B8" data-id="element-1013" />
              <Typography variant="body1" color="text.secondary" sx={{
              mt: 2
            }} data-id="element-1014">
                No state history available
              </Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-1015">
                The transaction has not progressed through any states yet
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>;
    }
    return <Accordion data-id="element-1016">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-1018" />} data-id="element-1017">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1019">
            <Icon icon="lucide:clock" width={20} color="#0052CC" data-id="element-1020" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-1021">
              Timeline
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-1022">
          <Box sx={{
          position: 'relative'
        }} data-id="element-1023">
            {/* Timeline line */}
            <Box sx={{
            position: 'absolute',
            left: 16,
            top: 24,
            bottom: 24,
            width: 2,
            backgroundColor: '#0052CC',
            opacity: 0.3
          }} data-id="element-1024" />
            {transaction.stateHistory.map((state, index) => {
            const isExpanded = expandedState === `${state.stateCode}-${index}`;
            const statusColors = getStatusColor(state.status);
            return <Box key={`${state.stateCode}-${index}`} sx={{
              position: 'relative',
              mb: 3
            }} data-id="element-1025">
                  {/* Timeline node */}
                  <Box sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: statusColors.color,
                border: '3px solid white',
                boxShadow: `0 0 0 2px ${statusColors.color}`,
                zIndex: 1
              }} data-id="element-1026" />
                  <Accordion expanded={isExpanded} onChange={() => setExpandedState(isExpanded ? null : `${state.stateCode}-${index}`)} sx={{
                ml: 5
              }} data-id="element-1027">
                    <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={16} data-id="element-1029" />} sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(79, 70, 229, 0.04)'
                  }
                }} data-id="element-1028">
                      <Box sx={{
                    width: '100%'
                  }} data-id="element-1030">
                        <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1
                    }} data-id="element-1031">
                          <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }} data-id="element-1032">
                            <Icon icon={getStateIcon(state.stateCode)} width={20} height={20} color={statusColors.color} data-id="element-1033" />
                            <Typography variant="subtitle2" fontWeight={600} data-id="element-1034">
                              {(state.stateCode || '').replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                          <Chip icon={<Icon icon={getStatusIcon(state.status)} width={12} data-id="element-1036" />} label={state.status} size="small" sx={{
                        backgroundColor: statusColors.bg,
                        color: statusColors.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22
                      }} data-id="element-1035" />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" data-id="element-1037">
                          {formatDate(state.timestamp)}
                        </Typography>
                        {state.failReasonCode && <Typography variant="body2" color="error.main" sx={{
                      mt: 1
                    }} data-id="element-1038">
                            {state.failReasonCode.replace(/_/g, ' ')}
                          </Typography>}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails data-id="element-1039">
                      <Grid container spacing={2} data-id="element-1040">
                        {state.primaryRef && <Grid item xs={12} sm={6} data-id="element-1041">
                            <Typography variant="caption" color="text.secondary" data-id="element-1042">
                              Primary Reference
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace" data-id="element-1043">
                              {state.primaryRef}
                            </Typography>
                          </Grid>}
                        {state.durationMs !== undefined && <Grid item xs={12} sm={6} data-id="element-1044">
                            <Typography variant="caption" color="text.secondary" data-id="element-1045">
                              Duration
                            </Typography>
                            <Typography variant="body2" data-id="element-1046">
                              {formatDuration(state.durationMs)}
                            </Typography>
                          </Grid>}
                        {state.attempts && <Grid item xs={12} sm={6} data-id="element-1047">
                            <Typography variant="caption" color="text.secondary" data-id="element-1048">
                              Attempts
                            </Typography>
                            <Typography variant="body2" data-id="element-1049">
                              {state.attempts}
                            </Typography>
                          </Grid>}
                        {state.requestSnippet && <Grid item xs={12} data-id="element-1050">
                            <Typography variant="caption" color="text.secondary" data-id="element-1051">
                              Request
                            </Typography>
                            <Paper variant="outlined" sx={{
                        p: 1,
                        mt: 0.5,
                        backgroundColor: '#F8FAFC',
                        overflow: 'auto'
                      }} data-id="element-1052">
                              <Typography variant="body2" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }} data-id="element-1053">
                                {state.requestSnippet}
                              </Typography>
                            </Paper>
                          </Grid>}
                        {state.responseSnippet && <Grid item xs={12} data-id="element-1054">
                            <Typography variant="caption" color="text.secondary" data-id="element-1055">
                              Response
                            </Typography>
                            <Paper variant="outlined" sx={{
                        p: 1,
                        mt: 0.5,
                        backgroundColor: '#F8FAFC',
                        overflow: 'auto'
                      }} data-id="element-1056">
                              <Typography variant="body2" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }} data-id="element-1057">
                                {state.responseSnippet}
                              </Typography>
                            </Paper>
                          </Grid>}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Box>;
          })}
          </Box>
        </AccordionDetails>
      </Accordion>;
  };
  const getAmendStatusColor = (status: string) => {
    const map: Record<string, {
      bg: string;
      color: string;
      icon: string;
    }> = {
      PENDING: {
        bg: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6',
        icon: 'lucide:clock'
      },
      APPROVED: {
        bg: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981',
        icon: 'lucide:check-circle'
      },
      REJECTED: {
        bg: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444',
        icon: 'lucide:x-circle'
      }
    };
    return map[status] || {
      bg: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B',
      icon: 'lucide:help-circle'
    };
  };
  const renderAmendmentsAccordion = () => {
    const amendments = transaction.amendments;
    if (!amendments || amendments.length === 0) {
      return <Accordion data-id="element-1058">
          <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-1060" />} data-id="element-1059">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1061">
              <Icon icon="lucide:file-diff" width={20} color="#8B5CF6" data-id="element-1062" />
              <Typography variant="subtitle2" fontWeight={600} data-id="element-1063">
                Amendments
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-id="element-1064">
            <Box sx={{
            py: 3,
            textAlign: 'center'
          }} data-id="element-1065">
              <Icon icon="lucide:file-check" width={40} height={40} color="#94A3B8" data-id="element-1066" />
              <Typography variant="body2" color="text.secondary" sx={{
              mt: 1.5
            }} data-id="element-1067">
                No amendments have been made to this transaction
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>;
    }
    return <Accordion data-id="element-1068">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-1070" />} data-id="element-1069">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1071">
            <Icon icon="lucide:file-diff" width={20} color="#8B5CF6" data-id="element-1072" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-1073">
              Amendments
            </Typography>
            <Chip label={`${amendments.length}`} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            color: '#8B5CF6'
          }} data-id="element-1074" />
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-1075">
          {amendments.map((amendment, idx) => {
          const statusStyle = getAmendStatusColor(amendment.status);
          return <Paper key={amendment.id} variant="outlined" sx={{
            borderRadius: 2,
            overflow: 'hidden',
            mb: idx < amendments.length - 1 ? 2 : 0,
            borderColor: statusStyle.color + '30'
          }} data-id="element-1076">
                {/* Amendment Header */}
                <Box sx={{
              px: 2,
              py: 1.5,
              backgroundColor: statusStyle.bg,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }} data-id="element-1077">
                  <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-1078">
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace" sx={{
                  fontSize: '0.8rem'
                }} data-id="element-1079">
                      {amendment.id}
                    </Typography>
                    <Chip icon={<Icon icon={statusStyle.icon} width={12} data-id="element-1081" />} label={amendment.status} size="small" sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color,
                  '& .MuiChip-icon': {
                    color: statusStyle.color
                  }
                }} data-id="element-1080" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" data-id="element-1082">
                    {formatDate(amendment.timestamp)}
                  </Typography>
                </Box>

                {/* Reason */}
                <Box sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #EEF2F7'
            }} data-id="element-1083">
                  <Typography variant="caption" color="text.secondary" sx={{
                display: 'block',
                mb: 0.3
              }} data-id="element-1084">
                    Maker's Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                fontSize: '0.85rem',
                fontStyle: 'italic'
              }} data-id="element-1085">
                    "{amendment.reason}"
                  </Typography>
                </Box>

                {/* Checker Comment */}
                {amendment.checkerComment && <Box sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #EEF2F7',
              backgroundColor: '#F8FAFC'
            }} data-id="element-1086">
                    <Typography variant="caption" color="text.secondary" sx={{
                display: 'block',
                mb: 0.3
              }} data-id="element-1087">
                      Checker's Comment
                    </Typography>
                    <Typography variant="body2" sx={{
                fontSize: '0.85rem',
                fontWeight: 500
              }} data-id="element-1088">
                      {amendment.checkerComment}
                    </Typography>
                  </Box>}

                {/* Changes Table - Old vs New */}
                <TableContainer data-id="element-1089">
                  <Table size="small" data-id="element-1090">
                    <TableHead data-id="element-1091">
                      <TableRow sx={{
                    backgroundColor: '#F8FAFC'
                  }} data-id="element-1092">
                        <TableCell sx={{
                      fontWeight: 600,
                      color: '#475569',
                      fontSize: '0.75rem',
                      py: 1
                    }} data-id="element-1093">
                          Field
                        </TableCell>
                        <TableCell sx={{
                      fontWeight: 600,
                      color: '#EF4444',
                      fontSize: '0.75rem',
                      py: 1
                    }} data-id="element-1094">
                          Old Value
                        </TableCell>
                        <TableCell sx={{
                      fontWeight: 600,
                      color: '#22C55E',
                      fontSize: '0.75rem',
                      py: 1
                    }} data-id="element-1095">
                          New Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-id="element-1096">
                      {amendment.changes.map((change, cIdx) => <TableRow key={cIdx} sx={{
                    '&:last-child td': {
                      borderBottom: 0
                    }
                  }} data-id="element-1097">
                          <TableCell sx={{
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      color: '#0F172A',
                      py: 1.2
                    }} data-id="element-1098">
                            {change.field}
                          </TableCell>
                          <TableCell sx={{
                      py: 1.2
                    }} data-id="element-1099">
                            <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.3,
                        borderRadius: 1,
                        backgroundColor: 'rgba(239, 68, 68, 0.06)',
                        border: '1px solid rgba(239, 68, 68, 0.15)'
                      }} data-id="element-1100">
                              <Icon icon="lucide:minus" width={10} color="#EF4444" data-id="element-1101" />
                              <Typography variant="body2" fontFamily="monospace" sx={{
                          fontSize: '0.75rem',
                          color: '#EF4444',
                          textDecoration: 'line-through'
                        }} data-id="element-1102">
                                {change.oldValue}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{
                      py: 1.2
                    }} data-id="element-1103">
                            <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.3,
                        borderRadius: 1,
                        backgroundColor: 'rgba(34, 197, 94, 0.06)',
                        border: '1px solid rgba(34, 197, 94, 0.15)'
                      }} data-id="element-1104">
                              <Icon icon="lucide:plus" width={10} color="#22C55E" data-id="element-1105" />
                              <Typography variant="body2" fontFamily="monospace" sx={{
                          fontSize: '0.75rem',
                          color: '#22C55E',
                          fontWeight: 600
                        }} data-id="element-1106">
                                {change.newValue}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>;
        })}
        </AccordionDetails>
      </Accordion>;
  };
  const renderActionButtons = () => {
    const buttons = [];
    if (transaction.lifecycleStatus === 'IN_REVIEW' && transaction.currentState === 'SCREENING') {
      buttons.push(<Button key="approve" variant="contained" color="success" startIcon={<Icon icon="lucide:check" width={16} data-id="element-1108" />} onClick={() => handleAction('approve')} disabled={actionLoading} data-id="element-1107">
          Approve
        </Button>, <Button key="reject" variant="outlined" color="error" startIcon={<Icon icon="lucide:x" width={16} data-id="element-1110" />} onClick={() => handleAction('reject')} disabled={actionLoading} data-id="element-1109">
          Reject
        </Button>);
    }
    if (transaction.lifecycleStatus === 'PENDING_INQUIRY') {
      buttons.push(<Button key="inquiry" variant="contained" startIcon={actionLoading ? <CircularProgress size={16} data-id="element-1112" /> : <Icon icon="lucide:search" width={16} data-id="element-1113" />} onClick={() => handleAction('inquiry')} disabled={actionLoading} data-id="element-1111">
          Run Inquiry
        </Button>);
    }
    if (transaction.lifecycleStatus === 'SUCCESS') {
      buttons.push(<Button key="download-eprc" variant="outlined" startIcon={<Icon icon="lucide:download" width={16} data-id="element-1115" />} onClick={() => handleAction('download-eprc')} disabled={actionLoading} data-id="element-1114">
          Download e-PRC
        </Button>, <Button key="reverse" variant="outlined" color="error" startIcon={<Icon icon="lucide:undo-2" width={16} data-id="element-1117" />} onClick={() => handleAction('reverse')} disabled={actionLoading} data-id="element-1116">
          Reverse
        </Button>);
    }
    return buttons;
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: isMobile ? '100%' : 560,
      maxWidth: '100%'
    }
  }} data-id="element-1118">
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-1119">
        {/* Header */}
        <Box sx={{
        p: 3,
        pb: 2
      }} data-id="element-1120">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2
        }} data-id="element-1121">
            <Typography variant="h6" data-id="element-1122">Transaction Details</Typography>
            <IconButton onClick={onClose} size="small" data-id="element-1123">
              <Icon icon="lucide:x" width={20} data-id="element-1124" />
            </IconButton>
          </Box>
          {/* Header Chips */}
          <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2
        }} data-id="element-1125">
            <Chip label={transaction.payoutType} size="small" sx={{
            backgroundColor: transaction.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(139, 92, 246, 0.1)',
            color: transaction.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
            fontWeight: 600
          }} data-id="element-1126" />
            <Chip label={transaction.lifecycleStatus.replace(/_/g, ' ')} size="small" sx={{
            backgroundColor: transaction.lifecycleStatus === 'SUCCESS' ? '#0B7923' : transaction.lifecycleStatus === 'FAILED' ? '#AE2A19' : transaction.lifecycleStatus === 'IN_REVIEW' || transaction.lifecycleStatus === 'PENDING_INQUIRY' ? '#B38600' : '#0052CC',
            color: 'white',
            fontWeight: 600
          }} data-id="element-1127" />
            <Chip label={`${transaction.currencyFCY} ${transaction.amountFCY}`} size="small" sx={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            color: '#10B981',
            fontWeight: 600
          }} data-id="element-1128" />
            <Chip label={transaction.corridor} size="small" sx={{
            backgroundColor: 'rgba(0, 82, 204, 0.1)',
            color: '#0052CC',
            fontWeight: 600
          }} data-id="element-1129" />
          </Box>
          {/* Subheader */}
          <Box data-id="element-1130">
            <Typography variant="subtitle2" color="text.secondary" gutterBottom data-id="element-1131">
              Remitting Institute (MTO)
            </Typography>
            {transaction.mtoName ? <Typography variant="h6" color="primary.main" gutterBottom data-id="element-1132">
                {transaction.mtoName}
              </Typography> : <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1
          }} data-id="element-1133">
                <Typography variant="h6" color="text.secondary" data-id="element-1134">
                  —
                </Typography>
                <Chip label="No MTO provided" size="small" sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              fontWeight: 600
            }} data-id="element-1135" />
              </Box>}
            <Typography variant="body2" color="text.secondary" data-id="element-1136">
              MTO Ref: {transaction.mtoRef} • Bank Ref: {transaction.bankRef}
            </Typography>
            {(transaction.railRefs.RRN || transaction.railRefs.RaastMsgId) && <Typography variant="body2" color="text.secondary" data-id="element-1137">
                Rail Refs:{' '}
                {[transaction.railRefs.RaastMsgId && `Raast: ${transaction.railRefs.RaastMsgId}`, transaction.railRefs.RRN && `RRN: ${transaction.railRefs.RRN}`, transaction.railRefs.STAN && `STAN: ${transaction.railRefs.STAN}`, transaction.railRefs.UETR && `UETR: ${transaction.railRefs.UETR}`].filter(Boolean).join(' • ')}
              </Typography>}
          </Box>
        </Box>
        <Divider data-id="element-1138" />
        {/* Accordion Content */}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 3
      }} data-id="element-1139">
          {renderLifecycleAccordion()}
          {renderOverviewAccordion()}
          {renderPartiesAccordion()}
          {renderAccountsAccordion()}
          {renderAmountsAccordion()}
          {renderComplianceAccordion()}
          {renderSettlementAccordion()}
          {renderAmendmentsAccordion()}
          {renderTimelineAccordion()}
        </Box>
        {/* Action Buttons */}
        {renderActionButtons().length > 0 && <>
            <Divider data-id="element-1140" />
            <Box sx={{
          p: 3,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap'
        }} data-id="element-1141">
              {renderActionButtons()}
            </Box>
          </>}
      </Box>
    </Drawer>;
};
export default TransactionDetailsDrawer;